import type { DRGetGameResponse } from '../api/getMyGames';
import type { PastResultItem } from './lobbyMyGamesPast';

export type ContestWeekOption = { id: string; label: string };

/**
 * Contest week label matching Daily Reveal's ContestSelector pills
 * (e.g. "Mar 11 - Mar 17, 2026").
 */
export function formatContestDateRange(
  days: Array<{ gameDate: string; dayNumber?: number }>
): string | null {
  if (!days || days.length === 0) return null;

  const sorted = [...days].sort((a, b) => {
    if (a.dayNumber != null && b.dayNumber != null && a.dayNumber !== b.dayNumber) {
      return a.dayNumber - b.dayNumber;
    }
    return a.gameDate.localeCompare(b.gameDate);
  });
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first?.gameDate || !last?.gameDate) return null;

  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = new Date(first.gameDate + 'T00:00:00').toLocaleDateString('en-US', opts);
  const endStr = new Date(last.gameDate + 'T00:00:00').toLocaleDateString('en-US', {
    ...opts,
    year: 'numeric',
  });
  return `${startStr} - ${endStr}`;
}

/** Last calendar day in a contest — used to sort weeks newest-first. */
export function contestLastGameDate(days: Array<{ gameDate: string }>): string {
  return days.reduce((max, d) => (d.gameDate > max ? d.gameDate : max), '');
}

/**
 * Week options for the Daily Reveal product (Big League Draw, Gridiron Draw, …) whose filter
 * chip is selected on Recent.
 *
 * Only contests that contributed at least one of `sectionItems` are listed.
 * Sorted newest-first so the default selection is the most recent week.
 */
export function buildDailyRevealContestWeekOptions(input: {
  sectionItems: PastResultItem[];
  games: Array<DRGetGameResponse | undefined>;
  gameIdByDayId: Map<string, string>;
}): ContestWeekOption[] {
  const gameIds = new Set<string>();
  for (const item of input.sectionItems) {
    if (item.kind !== 'daily_reveal') continue;
    const gameId = input.gameIdByDayId.get(item.day.id);
    if (gameId) gameIds.add(gameId);
  }

  const options: Array<ContestWeekOption & { lastDate: string }> = [];
  for (const game of input.games) {
    if (!game || !gameIds.has(game.game.id)) continue;
    options.push({
      id: game.game.id,
      label: formatContestDateRange(game.days) ?? 'Contest',
      lastDate: contestLastGameDate(game.days),
    });
  }

  return options
    .sort((a, b) => b.lastDate.localeCompare(a.lastDate))
    .map(({ id, label }) => ({ id, label }));
}

/** Keep the player's choice when still valid; otherwise fall back to the newest week. */
export function resolveSelectedContestWeekId(
  options: ContestWeekOption[],
  selectedId: string | null | undefined
): string | null {
  if (options.length === 0) return null;
  if (selectedId && options.some(o => o.id === selectedId)) return selectedId;
  return options[0]?.id ?? null;
}

/** Recent Daily Reveal cards for the contest week currently selected. */
export function filterDailyRevealItemsByContestWeek(
  items: PastResultItem[],
  contestWeekId: string | null,
  gameIdByDayId: Map<string, string>
): PastResultItem[] {
  if (!contestWeekId) return items;
  return items.filter(item => {
    if (item.kind !== 'daily_reveal') return true;
    return gameIdByDayId.get(item.day.id) === contestWeekId;
  });
}
